import { UserInputError } from "./errors-CEDWDXew.js";
import { ParamOrganizationSlug, ParamRegionUrl, ParamReplayId, ParamReplayUrl } from "./schema-DKEjeVqP.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { resolveScopedOrganizationSlug } from "./url-scope-CRM5sNo7.js";
import { resolveRegionUrlForOrganization } from "./resolve-region-url-DutLwiPb.js";
import { parseSentryUrl } from "./url-helpers-CcAcmPuu.js";
import { setTag } from "@sentry/core";

//#region src/tools/catalog/get-replay-details.ts
const MAX_ACTIVITY_EVENTS = 6;
const MAX_RELATED_ERRORS = 3;
const MAX_RELATED_TRACES = 2;
var get_replay_details_default = defineTool({
	name: "get_replay_details",
	skills: ["inspect"],
	requiredScopes: [
		"org:read",
		"project:read",
		"event:read"
	],
	requiredCapabilities: ["replays"],
	description: [
		"Get high-level information about a specific Sentry replay by URL or replay ID.",
		"",
		"USE THIS TOOL WHEN USERS:",
		"- Share a replay URL",
		"- Ask what happened in a specific replay",
		"- Want a concise replay summary plus the next issue or trace lookups to run",
		"",
		"<examples>",
		"### With replay URL",
		"```",
		"get_replay_details(replayUrl='https://my-organization.sentry.io/explore/replays/7e07485f-12f9-416b-8b14-26260799b51f/')",
		"```",
		"",
		"### With organization and replay ID",
		"```",
		"get_replay_details(organizationSlug='my-organization', replayId='7e07485f-12f9-416b-8b14-26260799b51f')",
		"```",
		"</examples>"
	].join("\n"),
	inputSchema: {
		replayUrl: ParamReplayUrl.optional(),
		organizationSlug: ParamOrganizationSlug.optional(),
		replayId: ParamReplayId.optional(),
		regionUrl: ParamRegionUrl.nullable().optional()
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	async handler(params, context) {
		const resolved = resolveReplayParams(params);
		const regionUrl = await resolveRegionUrlForOrganization({
			context,
			organizationSlug: resolved.organizationSlug,
			regionUrl: params.regionUrl
		});
		const apiService = apiServiceFromContext(context, { regionUrl: regionUrl ?? void 0 });
		setTag("organization.slug", resolved.organizationSlug);
		setTag("replay.id", resolved.replayId);
		const replay = await apiService.getReplayDetails({
			organizationSlug: resolved.organizationSlug,
			replayId: resolved.replayId
		});
		await assertReplayWithinProjectConstraint({
			apiService,
			organizationSlug: resolved.organizationSlug,
			replay,
			projectSlug: context.constraints.projectSlug
		});
		const isArchived = replay.is_archived === true;
		const projectId = replay.project_id != null ? String(replay.project_id) : null;
		const hasSegments = (replay.count_segments ?? 0) > 0;
		const [{ segments }, relatedIssues, relatedTraces] = await Promise.all([
			fetchReplaySegments({
				apiService,
				organizationSlug: resolved.organizationSlug,
				replayId: resolved.replayId,
				projectId,
				isArchived,
				hasSegments
			}),
			fetchReplayIssues({
				apiService,
				organizationSlug: resolved.organizationSlug,
				errorIds: replay.error_ids
			}),
			fetchReplayTraces({
				apiService,
				organizationSlug: resolved.organizationSlug,
				traceIds: replay.trace_ids
			})
		]);
		return formatReplayOutput({
			replay,
			organizationSlug: resolved.organizationSlug,
			replayUrl: params.replayUrl ?? apiService.getReplayUrl(resolved.organizationSlug, replay.id),
			segments,
			isArchived,
			relatedIssues,
			relatedTraces
		});
	}
});
function resolveReplayParams(params) {
	if (params.replayUrl) {
		const parsed = parseSentryUrl(params.replayUrl);
		if (parsed.type !== "replay" || !parsed.replayId) throw new UserInputError("Invalid replay URL. URL must point to a Sentry replay resource.");
		return {
			organizationSlug: resolveScopedOrganizationSlug({
				resourceLabel: "Replay",
				scopedOrganizationSlug: params.organizationSlug,
				urlOrganizationSlug: parsed.organizationSlug
			}),
			replayId: parsed.replayId
		};
	}
	if (!params.organizationSlug || !params.replayId) throw new UserInputError("Provide either `replayUrl` or both `organizationSlug` and `replayId`.");
	return {
		organizationSlug: params.organizationSlug,
		replayId: params.replayId
	};
}
async function assertReplayWithinProjectConstraint({ apiService, organizationSlug, replay, projectSlug }) {
	if (!projectSlug) return;
	if (replay.project_id == null) throw new UserInputError(`Replay is outside the active project constraint. Expected project "${projectSlug}".`);
	const project = await apiService.getProject({
		organizationSlug,
		projectSlugOrId: projectSlug
	});
	if (String(project.id) !== String(replay.project_id)) throw new UserInputError(`Replay is outside the active project constraint. Expected project "${projectSlug}".`);
}
function formatReplayOutput({ replay, organizationSlug, replayUrl, segments, isArchived, relatedIssues, relatedTraces }) {
	const lines = [];
	const user = replay.user?.display_name ?? replay.user?.email ?? replay.user?.username ?? replay.user?.id ?? "Anonymous User";
	const device = replay.device?.name ?? replay.device?.model ?? replay.device?.family ?? null;
	const activityEvents = extractReplayActivityEvents(segments);
	lines.push(`# Replay ${replay.id} in **${organizationSlug}**`);
	lines.push("");
	lines.push("## Summary");
	lines.push("");
	lines.push(`- **Replay URL**: ${replayUrl}`);
	lines.push(`- **Duration**: ${replay.duration != null ? formatDurationSeconds(replay.duration) : "Unknown"}`);
	lines.push(`- **Environment**: ${replay.environment ?? "Unknown"}`);
	lines.push(`- **Browser**: ${formatNameVersion(replay.browser?.name, replay.browser?.version)}`);
	lines.push(`- **OS**: ${formatNameVersion(replay.os?.name, replay.os?.version)}`);
	lines.push(`- **User**: ${user}`);
	if (replay.urls.length > 0) lines.push(`- **URLs**: ${replay.urls.slice(0, 3).join(", ")}`);
	if (device) lines.push(`- **Device**: ${device}`);
	if (replay.releases && replay.releases.length > 0) lines.push(`- **Release**: ${replay.releases[0]}`);
	if (replay.replay_type) lines.push(`- **Replay Type**: ${replay.replay_type}`);
	lines.push(`- **Errors**: ${replay.count_errors ?? 0}`);
	lines.push(`- **Rage Clicks**: ${replay.count_rage_clicks ?? 0}`);
	lines.push(`- **Dead Clicks**: ${replay.count_dead_clicks ?? 0}`);
	lines.push(`- **Warnings**: ${replay.count_warnings ?? 0}`);
	lines.push(`- **Infos**: ${replay.count_infos ?? 0}`);
	if (replay.count_segments != null) lines.push(`- **Recording Segments**: ${replay.count_segments}`);
	lines.push(`- **Archived**: ${isArchived ? "Yes" : "No"}`);
	if (replay.has_viewed != null) lines.push(`- **Viewed**: ${replay.has_viewed ? "Yes" : "No"}`);
	lines.push("");
	lines.push("## Activity");
	lines.push("");
	if (isArchived) lines.push("Recording is archived and not available for playback.");
	else if (activityEvents.length > 0) {
		const startTime = activityEvents[0]?.timestampMs ?? null;
		for (const event of activityEvents) {
			const prefix = event.timestampMs !== null && startTime !== null ? `${formatRelativeTime(event.timestampMs - startTime)} · ` : "";
			const details = event.details.length > 0 ? ` · ${event.details.join(" · ")}` : "";
			lines.push(`- ${prefix}\`${event.label}\`${details}`);
		}
	} else lines.push("No activity events recorded.");
	const hasRelated = relatedIssues.length > 0 || relatedTraces.length > 0;
	if (hasRelated) {
		lines.push("");
		lines.push("## Related");
		lines.push("");
		for (const ri of relatedIssues) if (ri.issue) lines.push(`- **${ri.issue.shortId}**: ${ri.issue.title}`);
		else lines.push(`- Event \`${ri.eventId}\``);
		for (const rt of relatedTraces) {
			const spanInfo = rt.traceMeta ? ` (${rt.traceMeta.span_count} spans)` : "";
			lines.push(`- Trace \`${rt.traceId}\`${spanInfo}`);
		}
		lines.push("");
		lines.push("Use `get_sentry_resource` to inspect any issue or trace listed above.");
	}
	return lines.join("\n");
}
function formatDurationSeconds(durationSeconds) {
	if (durationSeconds < 60) return `${durationSeconds}s`;
	const minutes = Math.floor(durationSeconds / 60);
	const seconds = durationSeconds % 60;
	return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}
async function fetchReplaySegments({ apiService, organizationSlug, replayId, projectId, isArchived, hasSegments }) {
	if (isArchived || !projectId || !hasSegments) return { segments: null };
	try {
		const segments = await apiService.getReplayRecordingSegments({
			organizationSlug,
			projectSlugOrId: projectId,
			replayId
		});
		return { segments };
	} catch {
		return { segments: null };
	}
}
async function fetchReplayIssues({ apiService, organizationSlug, errorIds }) {
	const ids = errorIds.slice(0, MAX_RELATED_ERRORS);
	return Promise.all(ids.map(async (eventId) => {
		try {
			const [issue] = await apiService.listIssues({
				organizationSlug,
				query: eventId,
				limit: 1
			});
			return {
				eventId,
				issue: issue ?? null
			};
		} catch {
			return {
				eventId,
				issue: null
			};
		}
	}));
}
async function fetchReplayTraces({ apiService, organizationSlug, traceIds }) {
	const ids = traceIds.slice(0, MAX_RELATED_TRACES);
	return Promise.all(ids.map(async (traceId) => {
		try {
			const traceMeta = await apiService.getTraceMeta({
				organizationSlug,
				traceId
			});
			return {
				traceId,
				traceMeta
			};
		} catch {
			return {
				traceId,
				traceMeta: null
			};
		}
	}));
}
function formatNameVersion(name, version) {
	if (name && version) return `${name} ${version}`;
	return name ?? version ?? "Unknown";
}
function extractReplayActivityEvents(segments) {
	if (!segments) return [];
	const events = [];
	for (const segment of segments) for (const event of segment) {
		const replayEvent = summarizeReplayEvent(event);
		if (replayEvent) events.push(replayEvent);
		if (events.length >= MAX_ACTIVITY_EVENTS) return events;
	}
	return events;
}
function summarizeReplayEvent(event) {
	const timestampMs = getEventTimestampMillis(event.timestamp);
	const data = event.data;
	const tag = data?.tag ?? "";
	const payload = data?.payload ?? null;
	if (tag) {
		const replayEvent = summarizeTaggedReplayEvent(tag, payload);
		if (replayEvent) return {
			timestampMs,
			...replayEvent
		};
	}
	if (event.type !== void 0 && data) {
		const href = data.href ?? null;
		if (href) return {
			timestampMs,
			label: "page.view",
			details: [`href=${href}`]
		};
	}
	return null;
}
function summarizeTaggedReplayEvent(tag, payload) {
	if (tag === "performanceSpan") {
		const op = firstString(payload?.op);
		const description = firstString(payload?.description);
		const durationMs = payload?.data?.duration ?? null;
		if (description || op) return {
			label: op ?? "performanceSpan",
			details: [description ? `description=${description}` : null, durationMs !== null ? `duration_ms=${durationMs}` : null].filter((value) => value !== null)
		};
	}
	if (tag === "ui.click") {
		const message = firstString(payload?.message, payload?.description);
		if (message) return {
			label: tag,
			details: [`message=${quoteDetail(message)}`]
		};
	}
	const knownKeys = [
		"message",
		"description",
		"category",
		"type"
	];
	const details = [];
	for (const key of knownKeys) {
		const value = firstString(payload?.[key]);
		if (value) details.push(`${key}=${quoteDetail(value)}`);
	}
	const extra = summarizeObject(payload, new Set(knownKeys));
	if (extra) details.push(extra);
	return details.length > 0 ? {
		label: tag,
		details
	} : null;
}
function getEventTimestampMillis(value) {
	if (typeof value !== "number") return null;
	return value > 1e12 ? value : value * 1e3;
}
function formatRelativeTime(offsetMs) {
	const offsetSeconds = Math.max(0, Math.round(offsetMs / 1e3));
	if (offsetSeconds < 60) return `T+${offsetSeconds}s`;
	const minutes = Math.floor(offsetSeconds / 60);
	const seconds = offsetSeconds % 60;
	return seconds > 0 ? `T+${minutes}m ${seconds}s` : `T+${minutes}m`;
}
function summarizeObject(value, excludedKeys = /* @__PURE__ */ new Set()) {
	if (!value) return null;
	const entries = Object.entries(value).filter(([key, nested]) => !excludedKeys.has(key) && (typeof nested === "string" || typeof nested === "number")).slice(0, 3).map(([key, nested]) => `${key}=${nested}`);
	return entries.length > 0 ? `payload=${quoteDetail(entries.join(", "))}` : null;
}
function firstString(...values) {
	for (const value of values) if (typeof value === "string" && value.trim()) return value.trim();
	return null;
}
function quoteDetail(value) {
	return JSON.stringify(value.trim());
}

//#endregion
export { get_replay_details_default, resolveReplayParams };
//# sourceMappingURL=get-replay-details-BWeEKq_j.js.map